import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { buildMenu, NavEntry, NavLink } from '../shell/nav-menu';
import { DisplayMode, ShellService } from '../shell/shell.service';
import { FeatureService } from '../services/feature.service';
import { GuidedTourService } from '../guided-tour/guided-tour.component';

const DEFAULT_QUICK_LINKS = ['create-inspection'];
const MOBILE_QUERY = '(max-width: 780px)';

@Component({
    selector: 'abe-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
    menu: NavEntry[] = [];
    quickLinks: NavLink[] = [];

    logoUrl = '';
    markCrop = false;   // the collapsed mark is the left part of the sidebar logo
    markUrl = '';
    companyName = '';

    openId: string = null;
    flyoutTop = 86;
    mobileOpen = false;

    private isInspector = false;
    private isAdministrator = false;
    private inspectionTabOrder: any;
    private permissions: string[] = [];
    private pinned: string[] = [];
    private currentUrl = '';
    private subscriptions: Subscription[] = [];

    constructor(
        private shell: ShellService,
        private router: Router,
        private el: ElementRef,
        private features: FeatureService,
        private tour: GuidedTourService
    ) { }

    ngOnInit() {
        const user = this.shell.user;
        this.isInspector = user.user_level == 'inspector';
        this.isAdministrator = user.user_level == 'administrator';
        this.rebuildMenu();

        // Quick Links, collapse and display modes are per-user preferences stored on the server
        // and shared with twin-ci; see ShellService.
        this.subscriptions.push(this.shell.preferences$.subscribe(() => {
            const pins = this.shell.get('quick_links', DEFAULT_QUICK_LINKS);
            this.pinned = Array.isArray(pins) ? pins : DEFAULT_QUICK_LINKS;
            this.refreshQuickLinks();
        }));
        this.shell.loadPreferences();

        this.subscriptions.push(this.shell.settings$.subscribe(settings => {
            this.inspectionTabOrder = settings.inspection_tab_order;
            this.companyName = settings.setting_company_name || '';
            // The sidebar logo only (the login logo is for the login page; mockup 390 x 96). The
            // collapsed rail shows its left part, the mark (mockup 92 x 96), by hiding the rest;
            // without a sidebar logo the favicon, then text.
            const sidebar = settings.settings_logo_sidebar_image;
            const favicon = settings.settings_favicon_image;
            this.logoUrl = ShellService.mediaLogo(sidebar);
            this.markUrl = ShellService.mediaLogo(sidebar || favicon);
            this.markCrop = !!sidebar;
            this.rebuildMenu();
        }));

        // Tenant feature flags load after startup; rebuild the menu when they arrive or change.
        this.subscriptions.push(this.features.changes$.subscribe(() => this.rebuildMenu()));
        if (!this.isAdministrator && !this.isInspector) {
            this.subscriptions.push(this.shell.permissions$.subscribe(permissions => {
                this.permissions = permissions;
                this.rebuildMenu();
            }));
        }

        this.currentUrl = this.router.url;
        this.subscriptions.push(this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
            this.currentUrl = e.urlAfterRedirects;
            this.openId = null;
            this.setMobileOpen(false);
        }));
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    // ---- Menu ----

    private rebuildMenu() {
        this.menu = buildMenu({
            isInspector: this.isInspector,
            isAdministrator: this.isAdministrator,
            inspectionTabOrder: this.inspectionTabOrder,
            apiSite: environment.apiSite,
            oldSite: environment.oldSite,
            permissions: this.permissions,
            hasFeature: key => this.features.has(key)
        });
        this.refreshQuickLinks();
    }

    private allLinks(): NavLink[] {
        const links: NavLink[] = [];
        this.menu.forEach(entry => (entry.sections || []).forEach(section => links.push(...section.links)));
        return links;
    }

    // Flyout parents have no URL of their own; highlight them when one of their Angular routes is open.
    // An entry that is a page with sub-pages (Reports → Report Library): active on any of them.
    isLandingActive(entry: NavEntry): boolean {
        return this.currentUrl.split('?')[0] === entry.route || this.isGroupActive(entry);
    }

    isGroupActive(entry: NavEntry): boolean {
        const path = this.currentUrl.split('?')[0];
        return (entry.sections || []).some(section => section.links.some(link => !!link.route && (path === link.route || path.indexOf(link.route + '/') === 0)));
    }

    toggleGroup(entry: NavEntry, button: HTMLElement) {
        if (this.openId === entry.id) {
            this.openId = null;
            return;
        }
        this.openId = entry.id;
        if (this.isMobile()) {
            return;
        }
        // Open level with the button, then pull it up if it would run off the bottom of the window.
        this.flyoutTop = Math.round(button.getBoundingClientRect().top);
        setTimeout(() => {
            const flyout: HTMLElement = this.el.nativeElement.querySelector('.im-nav-group.open .im-flyout');
            if (!flyout) {
                return;
            }
            const overflow = this.flyoutTop + flyout.offsetHeight - (window.innerHeight - 12);
            if (overflow > 0) {
                this.flyoutTop = Math.max(12, this.flyoutTop - overflow);
            }
        });
    }

    closeFlyout() {
        this.openId = null;
    }

    // ---- Quick links ----

    isPinned(link: NavLink): boolean {
        return this.pinned.indexOf(link.id) !== -1;
    }

    togglePin(link: NavLink, event: Event) {
        event.preventDefault();
        event.stopPropagation();
        const pins = this.isPinned(link) ? this.pinned.filter(id => id !== link.id) : this.pinned.concat(link.id);
        this.shell.set('quick_links', pins);   // saved per user, shared with twin-ci
    }

    // Only links that are in this user's menu can show up, so a pin never exposes a page the
    // user's role can't see. Pins made in twin-ci for pages this menu doesn't have are kept.
    private refreshQuickLinks() {
        const byId: { [id: string]: NavLink } = {};
        this.allLinks().forEach(link => byId[link.id] = link);
        this.quickLinks = this.pinned.map(id => byId[id]).filter(link => !!link);
    }

    // ---- Collapse / mobile ----

    get collapsed(): boolean {
        return this.shell.collapsed;
    }

    // ---- Display modes ----

    isModeOn(mode: DisplayMode): boolean {
        return this.shell.isModeOn(mode);
    }

    toggleMode(mode: DisplayMode) {
        this.shell.setMode(mode, !this.shell.isModeOn(mode));
    }

    toggleCollapsed() {
        this.openId = null;
        this.shell.setCollapsed(!this.shell.collapsed);
    }

    setMobileOpen(open: boolean) {
        this.mobileOpen = open;
        this.shell.setMobileMenuOpen(open);
    }

    private isMobile(): boolean {
        return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event) {
        if (this.openId && !this.el.nativeElement.contains(event.target)) {
            this.openId = null;
        }
    }

    startTour() {
        this.helpOpen = false;
        if (this.mobileOpen) {
            this.setMobileOpen(false);
        }
        this.tour.showWelcome();   // mockup: replay opens the introduction first
    }

    /** Milo help panel (mockup "Help"); the chat is coming soon. */
    helpOpen = false;

    toggleHelp(event: Event) {
        event.stopPropagation();
        this.helpOpen = !this.helpOpen;
        if (this.helpOpen && this.mobileOpen) {
            this.setMobileOpen(false);
        }
    }

    @HostListener('document:keydown.escape')
    onEscape() {
        this.helpOpen = false;
        this.openId = null;
        if (this.mobileOpen) {
            this.setMobileOpen(false);
        }
    }

    @HostListener('window:resize')
    onResize() {
        if (this.mobileOpen && !this.isMobile()) {
            this.setMobileOpen(false);
        }
    }

    trackById(index: number, item: { id: string }) {
        return item.id;
    }
}
